import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './ModeratorPanel.css';

const ModeratorPanel = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [stats, setStats] = useState({});
    const [posts, setPosts] = useState([]);
    const [comments, setComments] = useState([]);
    const [reports, setReports] = useState([]);

    const [loading, setLoading] = useState(false);

    const renderShell = (content) => (
        <div className="moderator-page">
            <div className="moderator-ambient" aria-hidden="true" />
            <div className="moderator-panel">
                {content}
            </div>
        </div>
    );

    // 權限檢查
    useEffect(() => {
        // 等待認證載入完成
        if (authLoading) {
            return;
        }
        
        if (!user) {
            navigate('/');
            return;
        }
        
        if (!user?.permissions?.can_moderate) {
            alert('您沒有版主權限，無法訪問此頁面');
            navigate('/');
            return;
        }
        
        loadModeratorData();
    }, [user, navigate, authLoading]);

    const loadModeratorData = async () => {
        setLoading(true);
        try {
            // 載入版主統計資料
            const statsResponse = await api.get('/users/moderate/dashboard/');
            setStats(statsResponse.data);
            
            // 在開發模式下顯示調試信息
            if (process.env.NODE_ENV === 'development') {
                console.log('版主控制台調試信息:', statsResponse.data.debug_info);
            }

            // 載入需要審核的內容 (這裡需要後端提供相應的 API)
            // const postsResponse = await api.get('/posts/pending-moderation/');
            // setPosts(postsResponse.data);

        } catch (error) {
            console.error('載入版主資料失敗:', error);
            
            // 如果是認證錯誤，提示用戶重新登入
            if (error.response?.status === 401) {
                alert('認證過期，請重新登入');
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                navigate('/login');
                return;
            }
            // 不要立即導航，讓使用者看到錯誤
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePost = async (postId) => {
        const reason = prompt('請輸入刪除原因:');
        if (!reason) return;

        try {
            await api.delete(`/users/moderate/posts/${postId}/delete/`, {
                data: { reason }
            });
            alert('文章已刪除');
            loadModeratorData();
        } catch (error) {
            console.error('刪除文章失敗:', error);
            alert('刪除失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    const handlePinPost = async (postId) => {
        try {
            await api.post(`/users/moderate/posts/${postId}/pin/`);
            alert('文章置頂狀態已更新');
            loadModeratorData();
        } catch (error) {
            console.error('置頂操作失敗:', error);
            alert('操作失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    // 如果正在載入認證，顯示載入中
    if (authLoading) {
        return renderShell(
            <>
                <header className="moderator-header">
                    <div className="moderator-header-glow" aria-hidden="true" />
                    <h1 className="moderator-title">版主管理面板</h1>
                    <p className="moderator-subtitle">正在準備您的版主控制台...</p>
                </header>
                <div className="moderator-loading">載入中...</div>
            </>
        );
    }

    // 如果沒有用戶或沒有權限，不顯示內容（因為會被重定向）
    if (!user || !user?.permissions?.can_moderate) {
        return null;
    }

    if (loading) {
        return renderShell(
            <>
                <header className="moderator-header">
                    <div className="moderator-header-glow" aria-hidden="true" />
                    <h1 className="moderator-title">版主管理面板</h1>
                    <p className="moderator-subtitle">歡迎，{user?.username} 版主</p>
                </header>
                <div className="moderator-loading">載入中...</div>
            </>
        );
    }

    return renderShell(
        <>
            <header className="moderator-header">
                <div className="moderator-header-glow" aria-hidden="true" />
                <h1 className="moderator-title">版主管理面板</h1>
                <p className="moderator-subtitle">歡迎，{user?.username} 版主</p>
                <div className="moderator-meta">
                    <span className="moderator-pill">👤 角色：{user?.permissions?.role_label || '版主'}</span>
                    <span className="moderator-pill">🛡️ 版面數量：{stats.moderated_topics_count || 0}</span>
                </div>
            </header>

            {/* 開發模式調試信息 */}
            {process.env.NODE_ENV === 'development' && stats.debug_info && (
                <div className="moderator-debug-panel">
                    <h4>🔧 調試信息 (開發模式)</h4>
                    <p>用戶ID: {stats.debug_info.user_id} | 角色: {stats.debug_info.user_role}</p>
                    <p>數據庫中總版面數: {stats.debug_info.total_topics_in_db}</p>
                    <p>分配給此用戶的版面數: {stats.debug_info.topics_assigned_to_user}</p>
                    <details>
                        <summary>所有版面的版主分配情況</summary>
                        <ul>
                            {stats.debug_info.all_topic_moderators.map((item, index) => (
                                <li key={index}>
                                    {item.topic_name}: {item.moderator_username || '無版主'} (ID: {item.moderator_id || 'N/A'})
                                </li>
                            ))}
                        </ul>
                    </details>
                </div>
            )}

            {/* 統計資訊 */}
            <div className="moderator-stats">
                <div className="stat-card">
                    <h3>總文章數</h3>
                    <div className="stat-number">{stats.total_posts || 0}</div>
                </div>
                <div className="stat-card">
                    <h3>總留言數</h3>
                    <div className="stat-number">{stats.total_comments || 0}</div>
                </div>
                <div className="stat-card">
                    <h3>待處理檢舉</h3>
                    <div className="stat-number">{stats.pending_reports || 0}</div>
                </div>
                <div className="stat-card">
                    <h3>我的版面</h3>
                    <div className="stat-number">
                        {stats.moderated_topics_count || 0}
                    </div>
                    <div className="stat-detail">
                        {stats.moderated_topics_count > 0 
                            ? `${stats.moderated_topics_count}個版面` 
                            : '暫無負責版面'
                        }
                        {stats.is_showing_all_topics && (
                            <div className="warning-text">
                                ⚠️ 版面未分配，顯示所有版面
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 分頁導航 */}
            <div className="moderator-tabs">
                <button 
                    className={activeTab === 'dashboard' ? 'active' : ''}
                    onClick={() => setActiveTab('dashboard')}
                >
                    📊 控制台
                </button>
                <button 
                    className={activeTab === 'posts' ? 'active' : ''}
                    onClick={() => setActiveTab('posts')}
                >
                    📝 文章管理
                </button>
                <button 
                    className={activeTab === 'comments' ? 'active' : ''}
                    onClick={() => setActiveTab('comments')}
                >
                    💬 留言管理
                </button>
                <button 
                    className={activeTab === 'reports' ? 'active' : ''}
                    onClick={() => setActiveTab('reports')}
                >
                    🚨 檢舉處理
                </button>
            </div>

            {/* 控制台分頁 */}
            {activeTab === 'dashboard' && (
                <div className="moderator-content">
                    <h2>版主控制台</h2>
                    <div className="dashboard-grid">
                        <div className="dashboard-item">
                            <h3>📈 今日活動</h3>
                            <div className="activity-list">
                                {stats.recent_activities && stats.recent_activities.length > 0 ? (
                                    stats.recent_activities.map((activity, index) => (
                                        <div key={index} className="activity-item">
                                            <span className="activity-time">{activity.time}</span>
                                            <span className="activity-desc">{activity.description}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="activity-item">
                                        <span className="activity-desc">今日暫無活動</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="dashboard-item">
                            <h3>🔧 快速操作</h3>
                            <div className="quick-actions">
                                <button className="quick-btn" onClick={() => setActiveTab('posts')}>
                                    管理文章
                                </button>
                                <button className="quick-btn" onClick={() => setActiveTab('reports')}>
                                    處理檢舉
                                </button>
                                <button className="quick-btn" onClick={() => setActiveTab('comments')}>
                                    管理留言
                                </button>
                            </div>
                        </div>
                        
                        <div className="dashboard-item">
                            <h3>📝 負責版面</h3>
                            {stats.is_showing_all_topics && (
                                <div className="warning-message">
                                    ⚠️ 注意：版面尚未分配給版主，目前顯示所有版面統計
                                </div>
                            )}
                            <div className="moderated-topics">
                                {stats.moderated_topics && stats.moderated_topics.length > 0 ? (
                                    <div className="topics-list">
                                        {stats.moderated_topics.map(topic => (
                                            <div key={topic.id} className={`topic-badge ${topic.is_assigned_to_me ? 'assigned-to-me' : 'not-assigned'}`}>
                                                <span className="topic-name">
                                                    {topic.name}
                                                    {topic.is_assigned_to_me && <span className="my-topic">👤</span>}
                                                </span>
                                                <span className="post-count">{topic.post_count} 篇文章</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-topics">
                                        <span>目前沒有版面資料</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="dashboard-item">
                            <h3>📋 版主指南</h3>
                            <div className="guide-list">
                                <div className="guide-item">• 定期檢查用戶檢舉</div>
                                <div className="guide-item">• 維護社群討論品質</div>
                                <div className="guide-item">• 適時置頂重要公告</div>
                                <div className="guide-item">• 協助解決用戶糾紛</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 文章管理分頁 */}
            {activeTab === 'posts' && (
                <div className="moderator-content">
                    <h2>文章管理</h2>
                    <div className="content-info">
                        <p>管理您負責版面的文章。總共 {stats.total_posts || 0} 篇文章</p>
                        <p>負責版面：{stats.moderated_topics ? stats.moderated_topics.map(t => t.name).join('、') : '無'}</p>
                    </div>
                    <div className="actions-panel">
                        <button className="action-btn" onClick={() => window.open('/posts', '_blank')}>
                            🗂️ 查看所有文章
                        </button>
                        <button className="action-btn" onClick={() => window.open('/', '_blank')}>
                            🏠 前往論壇首頁
                        </button>
                        <button className="action-btn" onClick={() => alert('請直接在文章頁面進行管理操作')}>
                            ℹ️ 管理說明
                        </button>
                    </div>
                </div>
            )}

            {/* 留言管理分頁 */}
            {activeTab === 'comments' && (
                <div className="moderator-content">
                    <h2>留言管理</h2>
                    <div className="content-info">
                        <p>管理您負責版面的留言。總共 {stats.total_comments || 0} 則留言</p>
                        <p>您可以在各個文章頁面直接管理留言</p>
                    </div>
                    <div className="actions-panel">
                        <button className="action-btn" onClick={() => window.open('/posts', '_blank')}>
                            🗂️ 查看所有文章留言
                        </button>
                        <button className="action-btn" onClick={() => alert('留言管理功能已整合在各文章頁面中，您可以直接在文章頁面刪除不當留言')}>
                            ℹ️ 管理說明
                        </button>
                        <button className="action-btn" onClick={() => window.open('/', '_blank')}>
                            🏠 前往論壇
                        </button>
                    </div>
                </div>
            )}

            {/* 檢舉處理分頁 */}
            {activeTab === 'reports' && (
                <div className="moderator-content">
                    <h2>檢舉處理</h2>
                    <div className="content-info">
                        <p>處理用戶檢舉，維護社群秩序。</p>
                        <p>目前有 <strong>{stats.pending_reports || 0}</strong> 個待處理檢舉</p>
                        {stats.pending_reports === 0 && (
                            <p className="success-message">🎉 太好了！目前沒有待處理的檢舉</p>
                        )}
                    </div>
                    <div className="actions-panel">
                        <button 
                            className={`action-btn ${stats.pending_reports > 0 ? 'urgent' : ''}`}
                            onClick={() => alert(stats.pending_reports > 0 ? '檢舉處理功能正在開發中，目前請聯繫管理員處理' : '目前沒有待處理檢舉')}
                        >
                            🚨 {stats.pending_reports > 0 ? `處理 ${stats.pending_reports} 個檢舉` : '檢舉處理'}
                        </button>
                        <button className="action-btn" onClick={() => alert('檢舉歷史功能開發中...')}>
                            📋 檢舉歷史
                        </button>
                        <button className="action-btn" onClick={() => alert('檢舉統計功能開發中...')}>
                            📊 檢舉統計
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModeratorPanel;
